import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { useAppearance } from '@/stores/appearance';
import { Icon } from '@/components/Icon';
import { ContextualSidebar } from './ContextualSidebar';
import { NAV, PHONE_TABS, canSeeNavItem, legacyHref, type NavItem } from './nav';
import { NotificationCenter } from './NotificationCenter';
import { NotificationRuntime } from './NotificationRuntime';
import { GlobalSearch, openGlobalSearch } from './GlobalSearch';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import { captureInstallPrompt } from './installPrompt';
import { NetworkStatusBar } from './NetworkStatusBar';
import { AvatarPopover } from './AvatarPopover';
import { useResource } from '@/features/orders/api';
import type { OrderPage } from '@/api/types';
import s from './AppShell.module.css';

function visible(items: NavItem[], can: (...p: string[]) => boolean, role?: string) {
  return items.filter((i) => canSeeNavItem(i, can, role));
}

function RailItem({ item, expanded = false, count, hot = false }: { item: NavItem; expanded?: boolean; count?: number; hot?: boolean }) {
  const { pathname } = useLocation();
  const matched = item.match?.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const inner = (
    <>
      <Icon name={item.icon} size={20} />
      <span className={s.lbl}>{expanded ? item.label : item.short}</span>
      {count !== undefined && count > 0 && <span className={`${s.navCount} ${hot ? s.navCountHot : ''}`}>{count}</span>}
    </>
  );
  const cls = [s.ri, item.tone ? s[`t_${item.tone}`] : ''].join(' ');
  if (item.to) {
    return (
      <NavLink to={item.to} end={item.to === '/'} className={({ isActive }) => `${cls} ${isActive || matched ? s.on : ''}`} title={count !== undefined && count > 0 ? `${item.label} ${count}` : item.label}>
        {inner}
      </NavLink>
    );
  }
  return (
    <a href={legacyHref(item.legacy)} className={cls} title={`${item.label}（老版）`}>
      {inner}
    </a>
  );
}

/** 桌面默认展开为 208px 导航，iPad 固定使用 68px rail。 */
function Rail({ tablet, expanded, onToggle, badges }: { tablet: boolean; expanded: boolean; onToggle: () => void; badges: Record<string, number | undefined> }) {
  const can = useSession((st) => st.can);
  const user = useSession((st) => st.user);
  const items = visible(NAV, can, user?.role);
  const home = items.filter((i) => i.key === 'home');
  const biz = items.filter((i) => i.group === 'biz' && i.key !== 'home' && i.key !== 'taobao');
  const taobao = items.filter((i) => i.key === 'taobao');
  const data = items.filter((i) => i.group === 'data');
  return (
    <nav className={`${s.rail} ${expanded ? s.railExpanded : ''}`} aria-label="模块">
      <div className={s.brand}><span>发</span>{expanded && <div className={s.brandText}><strong>贸易商进销存</strong><small>生意兴隆，日进万金</small></div>}</div>
      <div className={s.scroll}>
        {home.map((i) => <RailItem key={i.key} item={i} expanded={expanded} count={badges.home} hot />)}
        {expanded ? <><div className={s.sep} /><span className={s.cap}>业务</span></> : null}
        {biz.map((i) => <RailItem key={i.key} item={i} expanded={expanded} count={badges[i.key]} hot={i.key === 'exceptions'} />)}
        {taobao.length > 0 && <>{expanded ? <span className={s.cap}>独立业务线</span> : <div className={s.sep} />}{taobao.map((i) => <RailItem key={i.key} item={i} expanded={expanded} />)}</>}
        {data.length > 0 && <>{expanded ? <span className={s.cap}>资料与设置</span> : <div className={s.sep} />}{data.map((i) => <RailItem key={i.key} item={i} expanded={expanded} />)}</>}
      </div>
      {tablet && (
        <>
          <button type="button" className={s.searchRail} onClick={openGlobalSearch} aria-label="全局搜索"><Icon name="srch" size={20} /></button>
          <NotificationCenter tablet />
          <AvatarPopover />
        </>
      )}
      {!tablet && <button type="button" className={s.railToggle} onClick={onToggle} aria-label={expanded ? '收起导航' : '展开导航'} title={expanded ? '收起导航' : '展开导航'}><Icon name="chev" size={14} /><span>{expanded ? '收起导航' : ''}</span></button>}
    </nav>
  );
}

const ROLE_LABEL: Record<string, string> = {
  ADMIN: '管理员', SALES: '业务', FOLLOWER: '跟单',
  WAREHOUSE: '仓库', PURCHASE: '采购', VIEWER: '查看',
};

function DesktopHeader() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const user = useSession((st) => st.user);
  const item = NAV.find((nav) => nav.to && nav.to !== '/' &&
    (pathname === nav.to || pathname.startsWith(`${nav.to}/`) || nav.match?.some((path) => pathname === path || pathname.startsWith(`${path}/`))));
  const moduleName = item?.label || '主屏';
  const shipmentLedger = pathname === '/shipments' && new URLSearchParams(search).get('view') === 'ledger';
  const shipmentExceptions = pathname === '/shipments' && new URLSearchParams(search).get('view') === 'exceptions';
  const shipmentToday = pathname === '/shipments' && new URLSearchParams(search).get('view') === 'today';
  const shipmentWarehouse = pathname === '/shipments' && new URLSearchParams(search).get('view') === 'warehouse';
  const inventoryView = pathname === '/inventory' ? new URLSearchParams(search).get('view') : null;
  const pageName = pathname === '/shipments' ? (shipmentLedger ? '发货批次台账' : shipmentExceptions ? '批次含异常' : shipmentToday ? '今日到期' : shipmentWarehouse ? '按仓库' : '待发操作台')
    : pathname === '/users/migration-preview' ? '迁移对照'
    : pathname.startsWith('/shipments/') ? '发货批次详情'
      : pathname === '/products' ? '产品管理'
        : pathname === '/gallery' ? '产品画册'
          : pathname === '/products/new' ? '新建产品'
            : pathname.startsWith('/products/') && pathname.endsWith('/edit') ? '编辑产品'
              : pathname.startsWith('/products/') ? '产品详情'
                : pathname === '/bundles' ? '产品套装'
                  : pathname === '/bundles/new' ? '新建套装'
                    : pathname.startsWith('/bundles/') && pathname.endsWith('/edit') ? '编辑套装'
                      : pathname.startsWith('/bundles/') ? '套装详情'
      : pathname === '/inventory/warehouses' ? '按仓库明细'
        : pathname === '/inventory/changes' ? '变动汇总'
          : pathname === '/inventory/transit' ? '在途待入库'
        : pathname === '/inventory/transactions' ? '出入库流水'
          : pathname === '/inventory/shortage' ? '负可用'
          : pathname === '/inventory' ? (inventoryView === 'shortage' ? '负可用' : inventoryView === 'noStock' ? '无实际库存' : '库存总览')
            : pathname === '/exceptions/responsibility' ? '责任与损失'
            : moduleName;
  const role = user?.roleName || ROLE_LABEL[user?.role || ''] || user?.role;
  const [tabs, setTabs] = useState<{ path: string; href: string; label: string }[]>(() => {
    try {
      const saved: unknown = JSON.parse(localStorage.getItem('ims.v2.workspace-tabs') || '[]');
      return Array.isArray(saved) ? saved.filter((tab): tab is { path: string; href: string; label: string } =>
        typeof tab?.path === 'string' && tab.path.startsWith('/') && !tab.path.startsWith('//') && tab.path !== '/' &&
        typeof tab?.href === 'string' && tab.href.startsWith(tab.path) && !tab.href.startsWith('//') &&
        typeof tab?.label === 'string' && tab.label.length <= 80).slice(-8) : [];
    } catch { return []; }
  });

  useEffect(() => {
    if (pathname === '/') return;
    setTabs((current) => {
      const existing = current.find((tab) => tab.path === pathname);
      const next = existing
        ? current.map((tab) => tab.path === pathname ? { path: pathname, href: pathname + search, label: pageName } : tab)
        : [...current, { path: pathname, href: pathname + search, label: pageName }].slice(-8);
      return next;
    });
  }, [pathname, search, pageName]);

  useEffect(() => {
    try { localStorage.setItem('ims.v2.workspace-tabs', JSON.stringify(tabs)); } catch { /* storage may be disabled */ }
  }, [tabs]);

  const closeTab = (path: string) => {
    const remaining = tabs.filter((tab) => tab.path !== path);
    setTabs(remaining);
    if (pathname === path) navigate(remaining.at(-1)?.href || '/');
  };

  return (
    <>
      <header className={s.topbar}>
        <span className={s.crumb}>{moduleName !== pageName && <>{moduleName} / </>}<b>{pageName}</b></span>
        <div className={s.topRight}>
          <button className={s.topIcon} type="button" title="全局搜索（/）" aria-label="全局搜索" onClick={openGlobalSearch}><Icon name="srch" size={20} /></button>
          <NotificationCenter />
          <NavLink className={s.me} to="/me" title="我的账号">
            <span className={s.topAvatar}>{user?.name?.slice(0, 1) || '我'}</span>
            <span>{user?.name || user?.username || '我的账号'}</span>
            {role && <span className={s.role}>{role}</span>}
          </NavLink>
        </div>
      </header>
      <nav className={s.workspaceTabs} aria-label="工作区标签">
        <NavLink to="/" className={`${s.workspaceTab} ${pathname === '/' ? s.workspaceOn : ''}`}>主屏</NavLink>
        {tabs.map((tab) => <div key={tab.path} className={`${s.workspaceTab} ${pathname === tab.path ? s.workspaceOn : ''}`}>
          <NavLink to={tab.href} title={tab.label}>{tab.label}</NavLink>
          <button type="button" className={s.closeTab} onClick={() => closeTab(tab.path)} aria-label={`关闭${tab.label}标签`}>×</button>
        </div>)}
      </nav>
    </>
  );
}

/** 手机：底部 Tab（文档 23 §8.1）。详情等二级页由页面自己隐藏 Tab 栏 */
function TabBar({ homeCount }: { homeCount?: number }) {
  const { pathname } = useLocation();
  const warehouseRole = useSession((st) => st.user?.role === 'WAREHOUSE');
  const tabs = warehouseRole ? PHONE_TABS.map((tab) => tab.key === 'orders' ? { ...tab, key: 'work', label: '作业', icon: 'clip', to: '/work' } : tab) : PHONE_TABS;
  return (
    <nav className={s.tabbar} aria-label="主导航">
      {tabs.map((t) =>
        t.to ? (
          <NavLink key={t.key} to={t.to} end={t.to === '/'} className={({ isActive }) => `${s.tab} ${isActive || (t.key === 'apps' && pathname.startsWith('/reports')) ? s.on : ''}`}>
            <span className={s.tabIcon}><Icon name={t.icon} size={24} />{t.key === 'home' && homeCount !== undefined && homeCount > 0 && <span className={s.phoneBadge}>{homeCount}</span>}</span>
            {t.label}
          </NavLink>
        ) : (
          <a key={t.key} href={legacyHref(t.legacy!)} className={s.tab}>
            <Icon name={t.icon} size={24} />
            {t.label}
          </a>
        ),
      )}
    </nav>
  );
}

export function AppShell() {
  const ff = useFormFactor();
  const loc = useLocation();
  const userId = useSession((st) => st.user?.id);
  const user = useSession((st) => st.user);
  const can = useSession((st) => st.can);
  const mayReadOrders = can('orders.read.all', 'orders.read.own', 'orders.read.assigned');
  const orderSummary = useResource<OrderPage>(mayReadOrders ? '/api/orders?pageSize=1&includeStats=1' : null);
  const mayReadPurchase = can('purchase.read');
  const purchaseSummary = useResource<{ pagination: { total: number } }>(mayReadPurchase ? '/api/v2/purchase-orders/ready?pageSize=1' : null);
  const stats = orderSummary.data?.stats;
  const statusCount = (value: string) => stats?.statuses.find((item) => item.value === value)?.count;
  const visibleTodoCounts = [
    can('orders.approve') ? statusCount('SUBMITTED') : undefined,
    can('orders.change.approve') ? stats?.orderChanges.pendingReview : undefined,
    can('orders.change.acknowledge') ? stats?.orderChanges.pendingAcknowledgement : undefined,
    can('exceptions.read') ? stats?.exceptions.open : undefined,
  ];
  const todoCount = user?.role === 'ADMIN'
    ? stats ? (statusCount('SUBMITTED') ?? 0) + stats.orderChanges.pendingReview : undefined
    : user?.role === 'WAREHOUSE' || user?.role === 'PURCHASE'
      ? undefined
      : visibleTodoCounts.some((value) => value !== undefined)
        ? visibleTodoCounts.reduce<number>((total, value) => total + (value ?? 0), 0)
        : undefined;
  const navBadges: Record<string, number | undefined> = {
    home: todoCount,
    orders: stats?.all,
    exceptions: can('exceptions.read') ? stats?.exceptions.open : undefined,
    purchase: purchaseSummary.data?.pagination.total,
  };
  const appearance = useAppearance((st) => st.value);
  const [phoneTabBarHidden, setPhoneTabBarHidden] = useState(false);
  const [phoneTabBarPinned, setPhoneTabBarPinned] = useState(false);
  const [desktopNavCollapsed, setDesktopNavCollapsed] = useState(() => {
    try { return localStorage.getItem('ims.v2.desktop-nav-collapsed') === '1'; } catch { return false; }
  });
  const toggleDesktopNav = () => setDesktopNavCollapsed((collapsed) => {
    const next = !collapsed;
    try { localStorage.setItem('ims.v2.desktop-nav-collapsed', next ? '1' : '0'); } catch { /* 本机存储不可用时仍可切换 */ }
    return next;
  });
  useEffect(() => captureInstallPrompt(), []);
  useEffect(() => { setPhoneTabBarPinned(false); }, [loc.pathname]);
  useEffect(() => {
    if (userId) void useAppearance.getState().load(userId);
  }, [userId]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-size', `${appearance.fontSize}px`);
    root.style.setProperty('--line-h', `${appearance.lineHeight}px`);
    root.style.setProperty('--gap', `${appearance.gap}px`);
    root.style.setProperty('--m-fs', `${appearance.fontSize + 1}px`);
    root.style.setProperty('--m-lh', `${appearance.lineHeight + 1}px`);
    return () => { for (const key of ['--font-size', '--line-h', '--gap', '--m-fs', '--m-lh']) root.style.removeProperty(key); };
  }, [appearance]);
  // 手机上进入详情页隐藏 Tab 栏，底部让给操作条。
  // 详情页 = 最后一段是单号 / 编号（含数字），例如 /orders/cmu…、/shipments/SH-2026…；
  // /settlements/customers、/inventory/transactions 这类二级列表页保留 Tab 栏。
  const last = loc.pathname.split('/').filter(Boolean).pop() || '';
  const deep = loc.pathname.split('/').filter(Boolean).length >= 2 && /\d/.test(last);
  if (ff === 'phone') {
    // 设置二级页按原稿使用整屏列表，底部主 Tab 只保留在设置目录页。
    const settingsDetail = ['/settings/', '/users', '/audit-logs', '/imports'].some((path) =>
      loc.pathname.startsWith(path),
    );
    return (
      <div className={s.phone}>
        <NetworkStatusBar />
        <GlobalSearch />
        <NotificationRuntime />
        <main className={s.phoneMain}>
          <div className={`${s.pageViewport} ${s.phoneViewport}`}>
            <ContextualSidebar formFactor="phone" />
            <div className={s.pageOutlet}><Outlet context={{ setPhoneTabBarHidden, setPhoneTabBarPinned, navBadges }} /></div>
          </div>
        </main>
        {(!deep || phoneTabBarPinned) && !settingsDetail && !phoneTabBarHidden && <TabBar homeCount={navBadges.home} />}
      </div>
    );
  }
  const fullScreenOrderCreate = ff === 'tablet' && (loc.pathname === '/orders/new' || /^\/orders\/[^/]+\/edit$/.test(loc.pathname));
  const fullScreenBundleForm = ff === 'tablet' && (
    loc.pathname === '/bundles/new' || /^\/bundles\/[^/]+\/(?:edit|copy)$/.test(loc.pathname)
  );
  const fullScreenProductForm = ff === 'tablet' && (
    loc.pathname === '/products/new' || /^\/products\/[^/]+\/(?:edit|copy)$/.test(loc.pathname)
  );
  return (
    <div className={s.app}>
      <NetworkStatusBar />
      <GlobalSearch />
      <KeyboardShortcuts />
      <NotificationRuntime />
      {!fullScreenOrderCreate && !fullScreenBundleForm && !fullScreenProductForm && <Rail tablet={ff === 'tablet'} expanded={ff === 'desktop' && !desktopNavCollapsed} onToggle={toggleDesktopNav} badges={navBadges} />}
      <div className={s.main}>
        {ff === 'desktop' && <DesktopHeader />}
        <main className={s.content}>
          <div className={s.pageViewport}>
            {!fullScreenOrderCreate && !fullScreenBundleForm && !fullScreenProductForm && <ContextualSidebar formFactor={ff} />}
            <div className={s.pageOutlet}><Outlet context={{ navBadges, setPhoneTabBarPinned }} /></div>
          </div>
        </main>
      </div>
    </div>
  );
}
